import { useState, useMemo } from 'react';
import { IconButton } from '@/components/admin/icon-button/IconButton';
import { RichTextInputGroup } from '@/components/admin/input-groups/rich-text-input-group/RichTextInputGroup';
import { Button } from '@/components/admin/button/Button';
import { OurMission } from '@/pages/public/about-us-page/our-mission/OurMission';
import { WHO_WE_ARE_VALIDATION_FUNCTIONS } from '@/validation/admin/who-we-are-schema/WhoWeAreSchema';
import { getRichTextContentFromHtml } from '@/utils/functions/rich-text-content/rich-text-content';
import { returnDisplayedLocalization } from '@/utils/functions/localization/localization';
import { LocalizationLanguage } from '@/types/common/language';
import { ContentType } from '@/types/common/about-us';
import { Content } from '@/types/admin/who-we-are';
import { DEFAULT_LOCALE } from '@/const/common/locales';
import { ACTION_ICONS } from '@/const/common/action-icons';
import { COMMON_TEXT_ADMIN } from '@/const/admin/common';
import './DescriptionSection.scss';

export interface DescriptionSectionProps {
    content: Content[] | undefined;
    descriptionLimit: number;
    onChange: (data: Content) => void;
    onPublish: () => void;
    onTranslate: () => void;
    isPublishButtonActive: boolean;
    language: LocalizationLanguage;
}

export const DescriptionSection = ({
    content,
    onChange,
    descriptionLimit,
    onPublish,
    onTranslate,
    isPublishButtonActive,
    language,
}: DescriptionSectionProps) => {
    const [descriptionError, setDescriptionError] = useState<string | null>(null);
    const isBaseLanguage = language.code === DEFAULT_LOCALE;
    const descriptionContent = content?.find((item) => item.contentType === ContentType.Description);

    const displayedDescription = useMemo(() => {
        if (!descriptionContent) return null;
        const displayedLocalization = returnDisplayedLocalization(descriptionContent, language.code);
        return displayedLocalization?.description ?? descriptionContent.description;
    }, [language.code, descriptionContent]);

    if (!descriptionContent) {
        return null;
    }

    const handleDescriptionChange = (value: string) => {
        setDescriptionError(null);

        if (!isBaseLanguage) return;
        onChange({
            ...descriptionContent,
            description: value,
        });
    };

    const handleBlur = () => {
        if (!isBaseLanguage) return;
        const plainText = getRichTextContentFromHtml(displayedDescription ?? '');
        const error = WHO_WE_ARE_VALIDATION_FUNCTIONS.validateDescription(plainText);
        setDescriptionError(error || null);
    };

    const handlePublish = () => {
        const descriptionError = WHO_WE_ARE_VALIDATION_FUNCTIONS.validateDescription(
            getRichTextContentFromHtml(displayedDescription ?? ''),
        );

        setDescriptionError(descriptionError || null);

        if (descriptionError) {
            return;
        }

        onPublish();
    };

    const isPublishDisabled =
        !isBaseLanguage ||
        !isPublishButtonActive ||
        !!descriptionError ||
        !!WHO_WE_ARE_VALIDATION_FUNCTIONS.validateDescription(getRichTextContentFromHtml(displayedDescription ?? ''));

    return (
        <div className="description-section">
            <OurMission description={displayedDescription ?? ''} className="description-section-show-block" />
            <div className="description-section-textarea">
                <div className="description-section-textarea-buttons">
                    <IconButton
                        type="button"
                        className="translate-btn"
                        aria-label="Translate"
                        onClick={onTranslate}
                        DefaultIcon={ACTION_ICONS.translate.default}
                        disabled={isPublishButtonActive}
                    />
                </div>
                <RichTextInputGroup
                    key={`description-${language.code}`}
                    label={COMMON_TEXT_ADMIN.TYPE.DESCRIPTION}
                    isRequired
                    id={descriptionContent.id.toString()}
                    name={COMMON_TEXT_ADMIN.TYPE.DESCRIPTION}
                    value={displayedDescription ?? ''}
                    onChange={handleDescriptionChange}
                    onBlur={handleBlur}
                    maxLength={descriptionLimit}
                    error={isBaseLanguage ? (descriptionError ?? undefined) : undefined}
                    disabled={!isBaseLanguage}
                    hideToolbar={!isBaseLanguage}
                    normalizeSpaces
                    trimOnBlur
                />
                {isBaseLanguage && (
                    <Button
                        className="button"
                        buttonStyle={'primary'}
                        onClick={handlePublish}
                        type={'submit'}
                        disabled={isPublishDisabled}
                    >
                        {COMMON_TEXT_ADMIN.BUTTON.SAVE_AS_PUBLISHED}
                    </Button>
                )}
            </div>
        </div>
    );
};
